/*
Cell-panel derivation for the press check screen (PR 5b, task 5).

An email template's editable copy is derived server-side, per request, from the
htmlContent already in the document: `editableCells` is the list of <td>/<th>
cells that hold human copy, and `cellCoverage` is the fraction of the email's
visible text those cells account for. Nothing here is stored and nothing is
migrated - see docs/superpowers/plans/2026-09-01-email-campaigns-pr5b-cell-editing.md.

This file answers two questions for the editor: given what the server said,
WHICH state is this template's cell list in, and does the whole-document HTML
editor need to be open alongside the cells? And, at the bottom, given the
per-cell drafts the editor is holding: which of them are unsaved work, and
which point at a cell that is genuinely gone.

Why coverage and not cell count. Running the cell probe over all 86 PS
templates gives a sharply bimodal distribution, median 99.3%:

    0%            no cells at all              5 templates
    0.6 - 8.5%    exactly one trivial cell     6 templates
    20 - 60%      - nothing here -             0 templates
    77.8%         one cell                     1 template
    >= 95%                                    74 templates

The eleven bad ones are the *-newsletter-confirmation-* family and the
2024/2025 resident-discount emails, which write their copy as a direct child of
<table>/<tr> rather than inside a <td>. The browser foster-parents that markup
and findEditableCells correctly refuses it - so six of them surface exactly ONE
near-useless cell ("Descarga nuestra app"), which is worse than surfacing none:
a panel showing a single trivial cell implies the rest of the email is not
editable, when in truth the finder simply could not see it.

CELL_COVERAGE_THRESHOLD (0.9) is therefore set from the measured data, not
guessed. It sits in empty space - nothing lies between 8.5% and 77.8%, and
nothing between 77.8% and 96.8% - so it catches all twelve problem templates,
leaves the seventy-four good ones alone, and has wide margin on both sides so
ordinary editing will not flip a template across it.

An unknown coverage is treated as PARTIAL, never FULL. The failure this whole
file exists to prevent is a short list of cells silently claiming to be the
whole email; "we don't know" must never resolve in favour of that claim.

CommonJS on purpose: no bundler, runs under plain node src/utils/emailCells.test.js
(same pattern as src/utils/emailPlates.js).
*/

// How the cell fetch itself went. Same three-state discipline as
// emailPlates.js's plate strip, and for the same reason: a fetch that failed
// must not render as "this template has no editable copy".
const CELLS_LOADING = 'loading';
const CELLS_READY = 'ready';
const CELLS_FAILED = 'failed';

const CELL_MODE_LOADING = 'loading';
// A read is running but a previous one already answered: the cells on screen
// are real, just possibly one save out of date. This is NOT the same state as
// LOADING and must not render like it. The cell list is re-read after every
// save (a plate save, a cell save, a revision restore), and while that read is
// in flight the status is LOADING again - so collapsing this into LOADING
// blanks the panels mid-edit and takes any copy typed into an open one with
// them. Same discipline as emailPlates.js's loading/ready/failed strip: an
// empty list that is really a loading state must never render as a fact about
// the template.
const CELL_MODE_REFRESHING = 'refreshing';
const CELL_MODE_FAILED = 'failed';
const CELL_MODE_NONE = 'none';
const CELL_MODE_PARTIAL = 'partial';
const CELL_MODE_FULL = 'full';

const CELL_COVERAGE_THRESHOLD = 0.9;

const CELL_TITLE_CHARS = 90;

// Cell text is HTML-derived, so it arrives with the source document's newlines
// and runs of spaces in it. Collapse before measuring or truncating, otherwise
// a 40-character sentence padded with template indentation reads as a
// 300-character one.
const summariseCellText = (text, maxChars = CELL_TITLE_CHARS) => {
  const collapsed = String(text || '').replace(/\s+/g, ' ').trim();
  if (!collapsed) return '';
  if (collapsed.length <= maxChars) return collapsed;
  return `${collapsed.slice(0, maxChars - 1).trimEnd()}…`;
};

const buildCellPanels = ({ status, cells, coverage } = {}) => {
  // Only cells the server actually offered, and only ones the mutation can
  // target. editEmailTemplateCell is keyed on cellId; an entry without one
  // could be shown but never saved, and the probe over all 88 stored templates
  // (282 cells, zero identity failures) established that every cell offered is
  // a cell that can be written back. Showing an unsaveable cell would break
  // that promise, so drop it rather than render a dead editor.
  const offered = (Array.isArray(cells) ? cells : [])
    .filter(cell => cell && typeof cell.id === 'string' && cell.id !== '')
    .map((cell, index) => ({
      id: cell.id,
      ordinal: index + 1,
      text: cell.text || '',
      innerHtml: typeof cell.innerHtml === 'string' ? cell.innerHtml : '',
      chars: typeof cell.chars === 'number' ? cell.chars : String(cell.text || '').length,
      title: summariseCellText(cell.text),
    }));

  const knownCoverage = typeof coverage === 'number' && !Number.isNaN(coverage)
    ? coverage
    : null;

  // Below the threshold, or unmeasurable: the cells are not the whole email.
  const cellsAreShortOfTheEmail = knownCoverage === null
    || knownCoverage < CELL_COVERAGE_THRESHOLD;

  let mode;
  if (status === CELLS_FAILED) {
    mode = CELL_MODE_FAILED;
  } else if (status !== CELLS_READY) {
    mode = offered.length === 0 ? CELL_MODE_LOADING : CELL_MODE_REFRESHING;
  } else if (offered.length === 0) {
    mode = CELL_MODE_NONE;
  } else if (cellsAreShortOfTheEmail) {
    mode = CELL_MODE_PARTIAL;
  } else {
    mode = CELL_MODE_FULL;
  }

  return {
    mode,
    // A failed or never-answered fetch has nothing trustworthy to show. A
    // REFRESHING one does: the previous answer, which is what the editor is
    // already looking at.
    cells: mode === CELL_MODE_FAILED || mode === CELL_MODE_LOADING ? [] : offered,
    // Is this list a statement about the template? Only a completed read is.
    // Whether a cell is absent BECAUSE IT IS GONE - the one question that
    // decides whether an open cell editor may be closed and its typed copy
    // dropped - can only be answered from a list that is authoritative.
    authoritative: status === CELLS_READY,
    // Real cells, possibly one save out of date. Saving one now would target
    // an id the server may have renumbered, so the caller pauses cell saves
    // while this is true; nothing typed is discarded for it.
    stale: mode === CELL_MODE_REFRESHING,
    coverage: knownCoverage,
    coveragePercent: knownCoverage === null ? null : Math.round(knownCoverage * 100),
    // Whether to OPEN the whole-document HTML editor unprompted. It is always
    // *available* behind the panel's "Edit HTML" button - chrome edits need it
    // even at 100% coverage. This only says whether the screen should offer it
    // without being asked, which it must whenever the cells alone are not the
    // whole email (or we cannot tell).
    // REFRESHING answers from the coverage we already measured rather than
    // from the mode, so the whole-document editor does not fold itself away
    // and back on every save of a partial-coverage template.
    showHtmlEditor: mode === CELL_MODE_NONE
      || mode === CELL_MODE_PARTIAL
      || mode === CELL_MODE_FAILED
      || (mode === CELL_MODE_REFRESHING && cellsAreShortOfTheEmail),
  };
};

/*
Per-cell drafts.

The editor keeps one draft per cell id, not one for the whole list, so opening
a second cell cannot overwrite the first one's typed copy. This sorts a draft
map against the cells currently on screen and answers the only three questions
the UI has about it:

  pending    - the cell is here and the draft differs from what is stored:
               unsaved work, and the reason the beforeunload guard fires.
  orphans    - the cell is NOT in a list we trust, so it is really gone (ids
               are ordinals; an edit that changes a cell's length renumbers
               every cell after it). The draft is still the user's typed copy
               and is kept, to be handed back rather than dropped.
  unresolved - the cell is not in this list either, but the list is a refetch
               in progress and therefore not evidence of anything. Counts as
               unsaved work; never as an orphan.

A draft equal to its cell's stored innerHtml is not unsaved work - typing and
undoing leaves nothing to lose.
*/
const buildDraftState = ({ drafts, cells, authoritative } = {}) => {
  const draftMap = drafts && typeof drafts === 'object' ? drafts : {};
  const list = Array.isArray(cells) ? cells : [];
  const byId = {};
  list.forEach((cell) => {
    if (cell && typeof cell.id === 'string') byId[cell.id] = cell;
  });

  const pending = [];
  const orphans = [];
  const unresolved = [];

  Object.keys(draftMap).forEach((id) => {
    const value = draftMap[id];
    if (typeof value !== 'string') return;
    const cell = byId[id];
    if (cell) {
      if (value !== cell.innerHtml) {
        pending.push({ id, value, ordinal: cell.ordinal });
      }
    } else if (authoritative) {
      orphans.push({ id, value });
    } else {
      unresolved.push({ id, value });
    }
  });

  return {
    pending,
    orphans,
    unresolved,
    dirty: pending.length > 0 || orphans.length > 0 || unresolved.length > 0,
  };
};

module.exports = {
  CELLS_LOADING,
  CELLS_READY,
  CELLS_FAILED,
  CELL_MODE_LOADING,
  CELL_MODE_REFRESHING,
  CELL_MODE_FAILED,
  CELL_MODE_NONE,
  CELL_MODE_PARTIAL,
  CELL_MODE_FULL,
  CELL_COVERAGE_THRESHOLD,
  summariseCellText,
  buildCellPanels,
  buildDraftState,
};
